import { h, Fragment } from 'preact';
import { useCallback } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { getDefaultURLSearchRequest } from '@travel/traveler-core/utils/jrdpHelper';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import isNotEmptyArray from '@travel/ui/utils/arrays';
import { cx } from '@travel/utils';

import EmptyState from 'components/EmptyState';
import PaginationButton from 'components/PaginationButton';
import ProviderListSkeleton from 'components/ProviderListSkeleton';
import isLazyLoadContext from 'context/isLazyLoadContext';
import HotelCard from 'pages/HotelList/components/HotelCard';
import { fetchMoreHotels } from 'store/hotelList/actions';
import { getIsFetchingMoreHotels, hasMoreHotel } from 'store/hotelList/selectors';

import { FetchPriority } from 'constants/common';
import paths from 'constants/paths';
import { getDefaultURLCustomListRequest } from 'utils/search';

import styles from './list.module.scss';

import type { URLSearchQuery } from 'Common-Types';
import type { HotelList, RequestBody } from 'HotelList-Types';
type Props = {
  hotels: HotelList;
  isSP: boolean;
  isFetching: boolean;
  isShowingLoadingAnimation: boolean;
  pageIdentifier: string;
  urlSearchQuery?: URLSearchQuery;
  accommodationAreaPath: string;
  accommodationsPath: string;
  pageNumber: number;
};

function List(props: Props) {
  const {
    hotels,
    isSP,
    isFetching,
    isShowingLoadingAnimation,
    urlSearchQuery,
    pageIdentifier,
    accommodationAreaPath,
    accommodationsPath,
    pageNumber = 1,
  } = props;

  const hasShowMore = useSelector(hasMoreHotel);
  const isFetchingMoreHotels = useSelector(getIsFetchingMoreHotels);
  const HotelListUrl = paths.providerList.pathResolver(pageIdentifier);

  const { isPC } = useDeviceTypes();
  const dispatch = useDispatch();

  const handlePagination = useCallback(() => {
    const query = {
      ...getDefaultURLSearchRequest(urlSearchQuery),
      ...getDefaultURLCustomListRequest(urlSearchQuery),
      accommodationArea: pageIdentifier,
    } as RequestBody;

    dispatch(fetchMoreHotels(query));
  }, [pageIdentifier, dispatch, urlSearchQuery]);

  if (!isShowingLoadingAnimation && isFetching) {
    return (
      <div class={cx(!isSP && styles.container)}>
        <ProviderListSkeleton className={styles.skeletonContainer} isPC={isPC} />
        <ProviderListSkeleton className={styles.skeletonContainer} isPC={isPC} />
        {!isPC && <ProviderListSkeleton className={styles.skeletonContainer} isPC={isPC} />}
        <div class={styles.loading}>
          <LoadingCircle classType="primary" isLoading />
        </div>
      </div>
    );
  }

  return (
    <div className={cx(isShowingLoadingAnimation && styles.effectivelyHidden)}>
      {isNotEmptyArray(hotels.providers) ? (
        <section className={cx(!isSP && styles.container)} data-testid="hotelList-listContainer">
          <>
            {hotels.providers.map((hotel, index) => (
              <isLazyLoadContext.Provider
                key={hotel.id}
                value={{
                  isDisableLazyLoad: index === 0 ? true : false,
                  fetchPriority: index === 0 ? FetchPriority.HIGH : FetchPriority.LOW,
                }}
              >
                <Fragment key={hotel.id}>
                  <div className={styles.grayLine} />
                  <HotelCard
                    provider={hotel}
                    isSP={isSP}
                    query={urlSearchQuery}
                    isPlayableVideo={true}
                    accommodationAreaPath={accommodationAreaPath}
                    accommodationsPath={accommodationsPath}
                  />
                  <div className={styles.grayLine} />
                </Fragment>
              </isLazyLoadContext.Provider>
            ))}

            {isFetchingMoreHotels ? (
              <div class={styles.loading}>
                <LoadingCircle classType="primary" isLoading />
              </div>
            ) : (
              hasShowMore && (
                <PaginationButton
                  className={styles.seeMoreButton}
                  limit={hotels.limit}
                  offset={hotels.offset}
                  total={hotels.total}
                  onClick={handlePagination}
                  to={HotelListUrl}
                  pageNumber={pageNumber}
                />
              )
            )}
          </>
        </section>
      ) : (
        <EmptyState
          title={<Translate id="Hotel_List.No_Results.Sorry_Message" />}
          description={<Translate id="Hotel_List.No_Results.Change_Condition" />}
        />
      )}
    </div>
  );
}

export default List;
